<template>
    <v-card class="upload-card" elevation="0" dark>
        <header class="card-heading">
            <v-icon small>{{ mdiCloudUploadOutline }}</v-icon>
            <h2>Import a device list</h2>
        </header>
        <p class="card-description">Register multiple devices at once using the serial list template.</p>
        <v-select
            v-if="userIsOrgAdmin"
            v-model="uploadSerialToOrg"
            class="upload-organization"
            outlined
            dense
            hide-details
            return-object
            item-text="organizationName"
            label="Organization (required)"
            clearable
            color="#f0784c"
            :items="orgList"
            :loading="loading"
        >
            <template v-slot:prepend-inner>
                <v-icon small>{{ mdiAccountSupervisorCircle }}</v-icon>
            </template>
        </v-select>
        <div class="upload-guidance">
            <v-icon small>{{ mdiFileExcelOutline }}</v-icon>
            <div>
                <strong>CSV or Excel (.csv, .xls, .xlsx, .xlsb)</strong>
                <p
                    >Use the template columns for serial number, asset tag and location. Devices register automatically
                    after upload.</p
                >
            </div>
        </div>
        <p v-if="userIsOrgAdmin" class="upload-warning">
            <strong>Organization reassignment:</strong> Serials already registered in other organization branches will
            be removed from those branches and registered to the selected organization.
        </p>
        <div class="upload-actions">
            <v-btn
                class="upload-submit"
                color="#db5120"
                elevation="0"
                :loading="uploadLoading"
                :disabled="uploadSerialDisabled"
                @click="$refs.serialUpload.click()"
            >
                <v-icon left small>{{ mdiCloudUploadOutline }}</v-icon>
                Choose file & import
            </v-btn>
            <v-btn class="upload-download" outlined :disabled="uploadLoading" @click="$emit('download-format-example')">
                <v-icon left small>{{ mdiFileDownloadOutline }}</v-icon>
                Download template
            </v-btn>
            <input
                ref="serialUpload"
                class="upload-input"
                type="file"
                accept=".csv,.xls,.xlsx,.xlsb,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                aria-label="Import a device list"
                :disabled="uploadSerialDisabled"
                @change="handleFileChange"
            />
        </div>
        <div v-if="uploadLoading || uploadFileName" class="upload-status" role="status" aria-live="polite">
            <span
                >{{ uploadLoading ? 'Importing' : 'Selected file'
                }}<template v-if="uploadFileName">: {{ uploadFileName }}</template></span
            >
            <v-progress-linear v-if="uploadLoading" indeterminate color="#f0784c" height="3" />
        </div>
        <section v-if="uploadedSns.length" class="upload-results" aria-label="Import results">
            <h3>
                <v-icon small>{{ mdiCheckboxMarkedCircleOutline }}</v-icon>
                {{ uploadedSns.length }} device(s) {{ uploadLoading ? 'imported so far' : 'successfully registered' }}
            </h3>
            <v-data-table
                class="upload-table"
                dark
                dense
                disable-sort
                :mobile-breakpoint="0"
                :headers="uploadHeaders"
                :items="uploadedSns"
            />
        </section>
    </v-card>
</template>

<script>
import {
    mdiCloudUploadOutline,
    mdiFileExcelOutline,
    mdiCheckboxMarkedCircleOutline,
    mdiAccountSupervisorCircle,
    mdiFileDownloadOutline
} from '@mdi/js'

export default {
    name: 'UploadFileTab',
    props: {
        userIsOrgAdmin: { type: Boolean, required: true },
        orgList: { type: Array, default: () => [] },
        uploadLoading: { type: Boolean, default: false },
        uploadFileName: { type: String, default: '' },
        uploadedSns: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false }
    },
    data() {
        return {
            mdiCloudUploadOutline,
            mdiFileExcelOutline,
            mdiCheckboxMarkedCircleOutline,
            mdiAccountSupervisorCircle,
            mdiFileDownloadOutline,
            uploadSerialToOrg: '',
            uploadHeaders: [
                { text: 'Serial #', value: 'sn' },
                { text: 'Asset Tag', value: 'assetTag' },
                { text: 'Location', value: 'location' }
            ]
        }
    },
    computed: {
        uploadSerialDisabled() {
            return (
                this.uploadLoading ||
                (this.userIsOrgAdmin && (!this.uploadSerialToOrg || !this.uploadSerialToOrg.orgId))
            )
        }
    },
    methods: {
        handleFileChange(event) {
            const input = event.target
            const file = input.files && input.files[0]
            // The picker can be cancelled, or the organization cleared while it is open.
            if (file && !this.uploadSerialDisabled) {
                this.$emit('upload-file', file, this.uploadSerialToOrg ? this.uploadSerialToOrg.orgId : undefined)
            }
            input.value = ''
        }
    }
}
</script>

<style scoped>
.upload-card {
    width: 100%;
    min-width: 0;
    height: 100%;
    padding: 20px;
    border: 1px solid #3b3e47;
    border-radius: 12px !important;
    background: #282b33 !important;
    color: #f3f4f6;
    font-family: 'Inter Tight', 'Inter', sans-serif;
    font-size: 12px;
}
.card-heading,
.upload-results h3 {
    display: flex;
    align-items: center;
    gap: 8px;
}
.card-heading h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.3px;
}
.card-heading .v-icon,
.upload-results .v-icon {
    color: #f0784c;
}
.card-description {
    margin: 8px 0 16px;
    color: #b8bbc5;
    line-height: 1.6;
}
.upload-organization {
    margin-bottom: 12px;
}
.upload-guidance {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px;
    background: #22252d;
    border: 1px dashed #484c57;
    border-radius: 8px;
    line-height: 1.6;
}
.upload-guidance > .v-icon {
    margin-top: 2px;
    color: #f0784c;
}
.upload-guidance p {
    margin: 4px 0 0;
    color: #b8bbc5;
}
.upload-warning {
    margin: 12px 0 0;
    padding-left: 10px;
    border-left: 2px solid #db5120;
    color: #e7b39f;
    line-height: 1.6;
}
.upload-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;
}
.upload-input {
    display: none;
}
.upload-card ::v-deep .v-btn {
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
}
.upload-submit:hover:not(:disabled) {
    background-color: #f0784c !important;
}
.upload-download {
    color: #f0784c !important;
    border-color: #6e493c;
}
.upload-card ::v-deep .v-input,
.upload-card ::v-deep .v-label {
    font-family: inherit;
    font-size: 12px;
}
.upload-card ::v-deep .v-text-field--outlined > .v-input__control > .v-input__slot {
    min-height: 40px;
    background: #22252d;
    border-radius: 8px;
}
.upload-card ::v-deep .v-text-field--outlined:not(.v-input--is-focused) fieldset {
    border-color: #484c57;
}
.upload-card ::v-deep .v-input .v-icon {
    color: #b8bbc5;
}
.upload-status {
    display: grid;
    gap: 8px;
    margin-top: 16px;
    color: #b8bbc5;
    overflow-wrap: anywhere;
}
.upload-results {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #3b3e47;
}
.upload-results h3 {
    margin: 0 0 12px;
    font-size: 12px;
    font-weight: 600;
}
.upload-table {
    background: #22252d !important;
    border: 1px solid #3b3e47;
    border-radius: 8px;
}
.upload-table ::v-deep table {
    min-width: 380px;
}
.upload-table ::v-deep th,
.upload-table ::v-deep td {
    font-size: 12px !important;
    white-space: nowrap;
    border-bottom-color: #3b3e47 !important;
}
.upload-table ::v-deep th {
    color: #b8bbc5 !important;
}
.upload-table ::v-deep tbody tr:hover {
    background: #30343e !important;
}
.upload-table ::v-deep .v-data-footer {
    border-top-color: #3b3e47;
    font-size: 12px;
}
@media (max-width: 600px) {
    .upload-card {
        padding: 16px;
    }
    .upload-actions .v-btn {
        width: 100%;
    }
}
</style>
